import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { 
  Factory, Gem, BarChart3, Layers, AlertTriangle, Target, FileText,
  ArrowUpRight, ArrowDownRight, TrendingUp, TrendingDown, Info, Pickaxe, Package
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ComposedChart, Line, Legend
} from 'recharts';
import ExecutiveSummarySection from '../../components/ExecutiveSummarySection';
import PageHeader from '../../components/PageHeader';
import KPICardOperations from '../../components/KPICardOperations';
import RealVsBudgetTrajectory from '../../components/RealVsBudgetTrajectory';
import { plantRecoveryTrajectoryKpis, plantRecoveryData } from '../../data/mock/operationsData';
import { processKeyMessages } from '../../utils/iconMapping';

// ============ COMPONENTS ============

// Status Badge Component
const StatusBadge = ({ status, language }) => {
  const configs = {
    'conforme': { bg: 'bg-emerald-100', text: 'text-emerald-700', label: { pt: 'Conforme', en: 'On Track' } },
    'acompanhamento': { bg: 'bg-blue-100', text: 'text-blue-700', label: { pt: 'Acompanhamento', en: 'Monitoring' } },
    'pressao': { bg: 'bg-amber-100', text: 'text-amber-700', label: { pt: 'Pressão', en: 'Pressure' } },
  };
  const config = configs[status] || configs['conforme'];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[9px] font-semibold ${config.bg} ${config.text}`}>
      {config.label[language]}
    </span>
  );
};

// Severity Badge Component
const SeverityBadge = ({ severity, language }) => {
  const configs = {
    'high': { bg: 'bg-rose-100', text: 'text-rose-700', label: { pt: 'Alto', en: 'High' } },
    'medium': { bg: 'bg-amber-100', text: 'text-amber-700', label: { pt: 'Moderado', en: 'Moderate' } },
    'low': { bg: 'bg-emerald-100', text: 'text-emerald-700', label: { pt: 'Limitado', en: 'Limited' } },
  };
  const config = configs[severity] || configs['medium'];
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold ${config.bg} ${config.text}`}>
      {config.label[language]}
    </span>
  );
};

// ============ MAIN COMPONENT ============
const PlantRecovery = ({ year, setActiveSection, flowMap }) => {
  const { language } = useLanguage();
  const is2026 = year === 2026;
  const rawData = is2026 ? plantRecoveryData[2026] : plantRecoveryData[2025];
  
  // Process data: map icon keys to actual icon components
  const data = {
    ...rawData,
    keyMessages: processKeyMessages(rawData.keyMessages),
  };

  // Dados para o painel Real vs Trajectória do Orçamento
  const trajectoryKpisYear = is2026 ? plantRecoveryTrajectoryKpis[2026] : plantRecoveryTrajectoryKpis[2025];

  // Preparar dados para export PDF
  const pageDataForExport = {
    kpis: data.kpis,
    kpiLabels: {
      minerioTratado: language === 'pt' ? 'Minério Tratado' : 'Ore Processed',
      teorAlimentacao: language === 'pt' ? 'Teor de Alimentação' : 'Feed Grade',
      tamanhoMedioPedra: language === 'pt' ? 'Tamanho Médio da Pedra' : 'Avg Stone Size',
      pedrasEspeciais: language === 'pt' ? 'N.º de Pedras Especiais' : 'Special Stones',
    },
    keyMessages: data.keyMessages,
    constrangimentos: data.constrangimentos,
    ponteFinanceira: data.ponteFinanceira,
  };

  return (
    <div className="space-y-6" data-testid="plant-recovery">
      {/* Page Header */}
      <PageHeader
        title={language === 'pt' ? 'Planta e Recuperação' : 'Plant & Recovery'}
        period={data.period}
        subtitle={language === 'pt' 
          ? `vs Orçamento ${data.period} e comparativo com ${is2026 ? '1T25' : '2024'}`
          : `vs Budget ${data.period} and comparison with ${is2026 ? 'Q1 25' : '2024'}`}
        language={language}
        flowMap={flowMap}
        pageData={pageDataForExport}
      />

      {/* ============ MENSAGENS-CHAVE - McKinsey Premium Compact ============ */}
      <div className="bg-gradient-to-r from-[#0A192F] to-[#112240] rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-0.5 h-4 bg-gold-500 rounded-full"></div>
          <h3 className="text-white/90 font-medium text-xs uppercase tracking-[0.15em]">
            {language === 'pt' ? 'Mensagens-Chave' : 'Key Messages'}
          </h3>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {data.keyMessages.map((msg, idx) => {
            const MsgIcon = msg.icon;
            return (
              <div key={idx} className="bg-white/5 border border-white/10 rounded-lg p-3 hover:bg-white/8 transition-colors">
                <div className="flex items-start gap-2.5">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${msg.iconBg}`}>
                    <MsgIcon className={`w-4 h-4 ${msg.iconColor}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className={`font-semibold text-[10px] mb-1.5 uppercase tracking-wider ${msg.titleColor}`}>
                      {msg.title[language]}
                    </h4>
                    <p className="text-slate-300/90 text-[11px] leading-relaxed">{msg.text[language]}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ============ 4 KPIs OFICIAIS ============ */}
      <div className="grid grid-cols-4 gap-4">
        <KPICardOperations 
          label={language === 'pt' ? 'Minério Tratado' : 'Ore Processed'}
          value={data.kpis.minerioTratado.actual >= 1000000 
            ? (data.kpis.minerioTratado.actual / 1000000).toFixed(1) + 'M' 
            : (data.kpis.minerioTratado.actual / 1000).toFixed(0) + 'K'}
          unit={data.kpis.minerioTratado.unit}
          vsBudget={data.kpis.minerioTratado.variance}
          vsPrior={data.kpis.minerioTratado.yoyVariance}
          budgetLabel={is2026 ? 'vs Orç. 1T' : 'vs Orç.'}
          yoyLabel={is2026 ? 'vs 1T25' : 'YoY'}
          language={language}
          tooltipData={{
            title: language === 'pt' ? 'Minério Tratado' : 'Ore Processed',
            definition: language === 'pt' 
              ? 'Volume total de minério processado pela planta de tratamento no período.'
              : 'Total ore volume processed by the treatment plant in the period.',
            relevance: language === 'pt'
              ? 'Principal indicador de throughput operacional. Impacta directamente a produção de quilates e receita.'
              : 'Main operational throughput indicator. Directly impacts carat production and revenue.',
            currentReading: language === 'pt'
              ? `Tratamento de ${(data.kpis.minerioTratado.actual/1000000).toFixed(2)}M m³ (${data.kpis.minerioTratado.variance > 0 ? '+' : ''}${data.kpis.minerioTratado.variance}% vs orçamento).`
              : `Processing of ${(data.kpis.minerioTratado.actual/1000000).toFixed(2)}M m³ (${data.kpis.minerioTratado.variance > 0 ? '+' : ''}${data.kpis.minerioTratado.variance}% vs budget).`,
            drillDown: [
              { label: language === 'pt' ? 'Jan' : 'Jan', value: '368K m³', variance: -4.5 },
              { label: language === 'pt' ? 'Fev' : 'Feb', value: '355K m³', variance: -6.2 },
              { label: language === 'pt' ? 'Mar' : 'Mar', value: '385K m³', variance: -8.1 },
            ]
          }}
        />
        <KPICardOperations 
          label={language === 'pt' ? 'Teor de Alimentação' : 'Feed Grade'}
          value={data.kpis.teorAlimentacao.actual.toFixed(2)}
          unit={data.kpis.teorAlimentacao.unit}
          vsBudget={data.kpis.teorAlimentacao.variance}
          vsPrior={data.kpis.teorAlimentacao.yoyVariance}
          budgetLabel={is2026 ? 'vs Orç. 1T' : 'vs Orç.'}
          yoyLabel={is2026 ? 'vs 1T25' : 'YoY'}
          language={language}
          tooltipData={{
            title: language === 'pt' ? 'Teor de Alimentação' : 'Feed Grade',
            definition: language === 'pt' 
              ? 'Concentração de quilates por 100 toneladas de minério alimentado à planta (cpht).'
              : 'Carat concentration per 100 tonnes of ore fed to the plant (cpht).',
            relevance: language === 'pt'
              ? 'Indicador crítico de qualidade do minério. Teor baixo implica maior volume necessário para atingir metas de produção.'
              : 'Critical ore quality indicator. Low grade implies higher volume needed to meet production targets.',
            currentReading: language === 'pt'
              ? `Teor de ${data.kpis.teorAlimentacao.actual.toFixed(2)} cpht indica qualidade ${data.kpis.teorAlimentacao.variance >= 0 ? 'acima' : 'abaixo'} do esperado.`
              : `Grade of ${data.kpis.teorAlimentacao.actual.toFixed(2)} cpht indicates quality ${data.kpis.teorAlimentacao.variance >= 0 ? 'above' : 'below'} expectations.`,
            drillDown: [
              { label: 'Mina 3', value: '16.8 cpht', variance: 2.4 },
              { label: 'Mina 4', value: '14.2 cpht', variance: -5.8 },
              { label: 'Stockpile blend', value: '15.1 cpht', variance: -1.2 },
            ]
          }}
        />
        <KPICardOperations 
          label={language === 'pt' ? 'Tamanho Médio da Pedra' : 'Avg Stone Size'}
          value={data.kpis.tamanhoMedioPedra.actual.toFixed(2)}
          unit={data.kpis.tamanhoMedioPedra.unit}
          vsBudget={data.kpis.tamanhoMedioPedra.variance}
          vsPrior={data.kpis.tamanhoMedioPedra.yoyVariance}
          budgetLabel={is2026 ? 'vs Orç. 1T' : 'vs Orç.'}
          yoyLabel={is2026 ? 'vs 1T25' : 'YoY'}
          language={language}
          tooltipData={{
            title: language === 'pt' ? 'Tamanho Médio da Pedra' : 'Avg Stone Size',
            definition: language === 'pt' 
              ? 'Peso médio por diamante recuperado, medido em quilates por pedra.'
              : 'Average weight per recovered diamond, measured in carats per stone.',
            relevance: language === 'pt'
              ? 'Impacta directamente o preço médio de venda. Pedras maiores têm prémio de mercado significativo.'
              : 'Directly impacts average selling price. Larger stones command significant market premium.',
            currentReading: language === 'pt'
              ? `Média de ${data.kpis.tamanhoMedioPedra.actual.toFixed(2)} ct/pedra ${data.kpis.tamanhoMedioPedra.variance >= 0 ? 'favorece' : 'pressiona'} o preço médio de venda.`
              : `Average of ${data.kpis.tamanhoMedioPedra.actual.toFixed(2)} ct/stone ${data.kpis.tamanhoMedioPedra.variance >= 0 ? 'favours' : 'pressures'} average selling price.`,
            drillDown: [
              { label: language === 'pt' ? 'Regulares' : 'Regular', value: '0.35 ct/pedra' },
              { label: language === 'pt' ? 'Especiais' : 'Special', value: '2.8 ct/pedra' },
              { label: language === 'pt' ? 'Blend Total' : 'Total Blend', value: '0.42 ct/pedra' },
            ]
          }}
        />
        <KPICardOperations 
          label={language === 'pt' ? 'N.º de Pedras Especiais' : 'Special Stones'}
          value={data.kpis.pedrasEspeciais.actual}
          unit={data.kpis.pedrasEspeciais.unit}
          vsBudget={data.kpis.pedrasEspeciais.variance}
          vsPrior={data.kpis.pedrasEspeciais.yoyVariance}
          budgetLabel={is2026 ? 'vs Orç. 1T' : 'vs Orç.'}
          yoyLabel={is2026 ? 'vs 1T25' : 'YoY'}
          language={language}
          tooltipData={{
            title: language === 'pt' ? 'Pedras Especiais' : 'Special Stones',
            definition: language === 'pt' 
              ? 'Número de diamantes de grande dimensão ou qualidade excepcional (tipicamente >10.8 ct ou qualidade fancy).'
              : 'Number of large-size or exceptional quality diamonds (typically >10.8 ct or fancy quality).',
            relevance: language === 'pt'
              ? 'Pedras especiais representam até 25% da receita. Uma única pedra excepcional pode impactar significativamente o resultado.'
              : 'Special stones represent up to 25% of revenue. A single exceptional stone can significantly impact results.',
            currentReading: language === 'pt'
              ? `${data.kpis.pedrasEspeciais.actual} pedras especiais no período (${data.kpis.pedrasEspeciais.variance > 0 ? '+' : ''}${data.kpis.pedrasEspeciais.variance}% vs orçamento).`
              : `${data.kpis.pedrasEspeciais.actual} special stones in period (${data.kpis.pedrasEspeciais.variance > 0 ? '+' : ''}${data.kpis.pedrasEspeciais.variance}% vs budget).`,
            drillDown: [
              { label: language === 'pt' ? '+50ct' : '+50ct', value: '2 pedras' },
              { label: language === 'pt' ? '20-50ct' : '20-50ct', value: '8 pedras' },
              { label: language === 'pt' ? '10.8-20ct' : '10.8-20ct', value: '28 pedras' },
            ]
          }}
        />
      </div>

      {/* ============ REAL VS TRAJECTÓRIA DO ORÇAMENTO ============ */}
      <RealVsBudgetTrajectory 
        year={year}
        kpis={trajectoryKpisYear}
        defaultKpi="minerioTratado"
      />

      {/* ============ SUMÁRIO EXECUTIVO - Formato Demonstração de Resultados ============ */}
      <ExecutiveSummarySection
        title={language === 'pt' ? 'Sumário Executivo' : 'Executive Summary'}
        subtitle={language === 'pt' ? 'Planta e recuperação' : 'Plant and recovery'}
        language={language}
        maxHeight="500px"
        icon={Factory}
        dataTestId="plant-recovery-executive-summary"
      >
        {/* Executive Overview */}
        <div className="mb-6">
          <p className="text-slate-700 leading-relaxed text-sm mb-4">
            {language === 'pt' 
              ? `Esta página acompanha se o minério disponível está a ser tratado ao ritmo esperado e convertido em quilates e qualidade comercial. A leitura deve combinar volume tratado, teor de alimentação e qualidade do output recuperado. O CFO deve perceber se a planta está a proteger ou a pressionar receita e margem.`
              : `This page tracks whether available ore is being processed at the expected rate and converted to carats and commercial quality. The reading should combine processed volume, feed grade and recovered output quality. The CFO should understand if the plant is protecting or pressuring revenue and margin.`}
          </p>
          <p className="text-slate-700 leading-relaxed text-sm">
            {language === 'pt' 
              ? `O ${data.period} apresenta sinais de atenção: minério tratado ${data.kpis.minerioTratado.variance > 0 ? '+' : ''}${data.kpis.minerioTratado.variance}% vs orçamento, teor de alimentação ${data.kpis.teorAlimentacao.variance > 0 ? '+' : ''}${data.kpis.teorAlimentacao.variance}% vs plano, e tamanho médio da pedra ${data.kpis.tamanhoMedioPedra.variance > 0 ? '+' : ''}${data.kpis.tamanhoMedioPedra.variance}% vs esperado. Os desvios podem resultar do feed, do blend ou de constrangimentos do circuito de tratamento.`
              : `${data.period} shows warning signs: ore processed ${data.kpis.minerioTratado.variance > 0 ? '+' : ''}${data.kpis.minerioTratado.variance}% vs budget, feed grade ${data.kpis.teorAlimentacao.variance > 0 ? '+' : ''}${data.kpis.teorAlimentacao.variance}% vs plan, and average stone size ${data.kpis.tamanhoMedioPedra.variance > 0 ? '+' : ''}${data.kpis.tamanhoMedioPedra.variance}% vs expected. Deviations may result from feed, blend or processing circuit constraints.`}
          </p>
        </div>

        {/* Driver Sections */}
        <div className="space-y-4">
          {/* Throughput e Conversão */}
          <div className="bg-gradient-to-r from-blue-50 to-slate-50 rounded-xl p-5 border border-blue-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Factory className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Throughput e Conversão' : 'Throughput and Conversion'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'A planta está a converter o feed em output ao ritmo esperado?' : 'Is the plant converting feed to output at expected rate?'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? `O minério tratado totalizou ${(data.throughput?.ytd?.totalPlanta?.real || data.kpis.minerioTratado.actual/1000).toLocaleString()}K m³ contra ${(data.throughput?.ytd?.totalPlanta?.orc || data.kpis.minerioTratado.plan/1000).toLocaleString()}K m³ orçamentados (${data.kpis.minerioTratado.variance > 0 ? '+' : ''}${data.kpis.minerioTratado.variance}%). A alimentação ao DMS foi de ${data.throughput?.ytd?.dmsAlimentacao?.real || 'N/D'}K m³ vs ${data.throughput?.ytd?.dmsAlimentacao?.orc || 'N/D'}K m³ planeados.`
                : `Ore processed totaled ${(data.throughput?.ytd?.totalPlanta?.real || data.kpis.minerioTratado.actual/1000).toLocaleString()}K m³ against ${(data.throughput?.ytd?.totalPlanta?.orc || data.kpis.minerioTratado.plan/1000).toLocaleString()}K m³ budgeted (${data.kpis.minerioTratado.variance > 0 ? '+' : ''}${data.kpis.minerioTratado.variance}%). DMS feed was ${data.throughput?.ytd?.dmsAlimentacao?.real || 'N/A'}K m³ vs ${data.throughput?.ytd?.dmsAlimentacao?.orc || 'N/A'}K m³ planned.`}
            </p>
            {data.throughput?.ytd && (
              <div className="bg-white/60 rounded-lg p-3 border border-blue-100">
                <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Resumo do Período' : 'Period Summary'}</p>
                <ul className="text-xs text-slate-600 space-y-1">
                  <li className="flex items-center gap-2">
                    <div className={`w-1.5 h-1.5 rounded-full ${data.throughput.ytd.totalPlanta.variance >= 0 ? 'bg-emerald-500' : 'bg-rose-500'}`}></div>
                    {language === 'pt' ? `Total Planta: ${data.throughput.ytd.totalPlanta.real}K vs ${data.throughput.ytd.totalPlanta.orc}K m³ orç. (${data.throughput.ytd.totalPlanta.variance > 0 ? '+' : ''}${data.throughput.ytd.totalPlanta.variance}%)` : `Total Plant: ${data.throughput.ytd.totalPlanta.real}K vs ${data.throughput.ytd.totalPlanta.orc}K m³ bud. (${data.throughput.ytd.totalPlanta.variance > 0 ? '+' : ''}${data.throughput.ytd.totalPlanta.variance}%)`}
                  </li>
                  <li className="flex items-center gap-2">
                    <div className={`w-1.5 h-1.5 rounded-full ${data.throughput.ytd.dmsAlimentacao.variance >= 0 ? 'bg-emerald-500' : 'bg-rose-500'}`}></div>
                    {language === 'pt' ? `DMS Alimentação: ${data.throughput.ytd.dmsAlimentacao.real}K vs ${data.throughput.ytd.dmsAlimentacao.orc}K m³ orç.` : `DMS Feed: ${data.throughput.ytd.dmsAlimentacao.real}K vs ${data.throughput.ytd.dmsAlimentacao.orc}K m³ bud.`}
                  </li>
                  <li className="flex items-center gap-2">
                    <div className={`w-1.5 h-1.5 rounded-full ${data.throughput.ytd.quilates.variance >= 0 ? 'bg-emerald-500' : 'bg-rose-500'}`}></div>
                    {language === 'pt' ? `Quilates Recuperados: ${data.throughput.ytd.quilates.real.toLocaleString()} vs ${data.throughput.ytd.quilates.orc.toLocaleString()} cts orç.` : `Carats Recovered: ${data.throughput.ytd.quilates.real.toLocaleString()} vs ${data.throughput.ytd.quilates.orc.toLocaleString()} cts bud.`}
                  </li>
                </ul>
              </div>
            )}
          </div>

          {/* Qualidade do Produto Recuperado */}
          <div className="bg-gradient-to-r from-emerald-50 to-slate-50 rounded-xl p-5 border border-emerald-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Gem className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Qualidade do Produto Recuperado' : 'Recovered Product Quality'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'A qualidade do output está a proteger receita e margem?' : 'Is output quality protecting revenue and margin?'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? `O teor de alimentação ficou em ${data.qualidade.teor.actual} cpht vs ${data.qualidade.teor.plan} cpht planeado (${data.qualidade.teor.variance > 0 ? '+' : ''}${data.qualidade.teor.variance}%). O tamanho médio da pedra foi de ${data.qualidade.tamanhoMedio.actual} ct/pedra contra ${data.qualidade.tamanhoMedio.plan} ct/pedra esperado. Foram recuperadas ${data.qualidade.especiais.actual} pedras especiais vs ${data.qualidade.especiais.plan} planeadas.`
                : `Feed grade was ${data.qualidade.teor.actual} cpht vs ${data.qualidade.teor.plan} cpht planned (${data.qualidade.teor.variance > 0 ? '+' : ''}${data.qualidade.teor.variance}%). Average stone size was ${data.qualidade.tamanhoMedio.actual} ct/stone against ${data.qualidade.tamanhoMedio.plan} ct/stone expected. ${data.qualidade.especiais.actual} special stones were recovered vs ${data.qualidade.especiais.plan} planned.`}
            </p>
            {data.qualidade.sfdReading && (
              <div className="bg-white/60 rounded-lg p-3 border border-emerald-100">
                <p className="text-xs font-semibold text-slate-700 mb-1">{language === 'pt' ? 'Leitura SFD' : 'SFD Reading'}</p>
                <p className="text-xs text-slate-600 italic">{data.qualidade.sfdReading[language]}</p>
              </div>
            )}
          </div>

          {/* Feed e Mix Operacional - Only for 2026 */}
          {is2026 && data.feedMix && (
            <div className="bg-gradient-to-r from-amber-50 to-slate-50 rounded-xl p-5 border border-amber-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Layers className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Feed e Mix Operacional' : 'Feed and Operational Mix'}</h4>
                  <p className="text-xs text-slate-500">{language === 'pt' ? 'O resultado foi condicionado pela qualidade do feed ou pela performance do circuito?' : 'Was result conditioned by feed quality or circuit performance?'}</p>
                </div>
              </div>
              <p className="text-slate-600 text-xs leading-relaxed mb-3">{data.feedMix.blendReading[language]}</p>
              <div className="bg-white/60 rounded-lg p-3 border border-amber-100 mb-3">
                <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Alocação por Origem' : 'Allocation by Source'}</p>
                <div className="grid grid-cols-2 gap-2">
                  {data.feedMix.origens.map((origem, idx) => (
                    <div key={idx} className="flex items-center justify-between text-xs">
                      <span className="text-slate-600">{origem.nome}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-700">{origem.percentagem}%</span>
                        <StatusBadge status={origem.status} language={language} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <p className="text-[10px] text-slate-500 flex items-center gap-1">
                <Info className="w-3 h-3" />
                {data.feedMix.observations[language]}
              </p>
            </div>
          )}
        </div>
      </ExecutiveSummarySection>

      {/* ============ BLOCO 1: THROUGHPUT E CONVERSÃO - 2 VISUAIS DISTINTOS ============ */}
      {is2026 && data.throughput?.monthly && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <BarChart3 className="w-4 h-4 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 text-sm">{language === 'pt' ? 'Throughput e Output da Planta' : 'Plant Throughput and Output'}</h3>
              <p className="text-[10px] text-slate-500">{language === 'pt' ? 'Comparar primeiro o minério tratado vs plano; depois verificar se os quilates acompanharam o volume' : 'First compare ore processed vs plan; then check if carats followed volume'}</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-6">
            {/* VISUAL A — Minério Tratado vs Plano */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="font-semibold text-slate-700 text-xs">{language === 'pt' ? 'Minério Tratado vs Plano' : 'Ore Processed vs Plan'}</h4>
                  <p className="text-[10px] text-slate-400">{language === 'pt' ? 'Volume processado pela planta' : 'Volume processed by plant'}</p>
                </div>
                <div className="flex items-center gap-3 text-[10px]">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm bg-gold-500"></div>
                    <span className="text-slate-600">{language === 'pt' ? 'Real' : 'Actual'}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm bg-gold-500/30 border border-gold-400/50"></div>
                    <span className="text-slate-600">{language === 'pt' ? 'Orçamento' : 'Budget'}</span>
                  </div>
                </div>
              </div>
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.throughput.monthly} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 10 }} />
                    <YAxis 
                      tick={{ fill: '#64748b', fontSize: 10 }} 
                      tickFormatter={(v) => `${v}`}
                      label={{ value: "m³ ('000)", angle: -90, position: 'insideLeft', style: { fontSize: 9, fill: '#94a3b8' } }}
                    />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#0A192F', border: 'none', borderRadius: '8px', fontSize: '11px' }}
                      labelStyle={{ color: '#C5A059' }}
                      formatter={(value) => [`${value.toLocaleString()} K m³`, '']}
                    />
                    <Bar dataKey="tratadoReal" name={language === 'pt' ? 'Minério Tratado Real' : 'Actual Ore Processed'} fill="#C5A059" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="tratadoOrc" name={language === 'pt' ? 'Minério Tratado Orçamento' : 'Budget Ore Processed'} fill="#C5A05940" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {/* YTD Summary for Minério */}
              <div className="mt-3 p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-slate-500 font-medium">{language === 'pt' ? 'Acumulado Período:' : 'Period YTD:'}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-700">
                      {data.throughput.ytd.totalPlanta.real}K vs {data.throughput.ytd.totalPlanta.orc}K m³
                    </span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${data.throughput.ytd.totalPlanta.variance >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                      {data.throughput.ytd.totalPlanta.variance > 0 ? '+' : ''}{data.throughput.ytd.totalPlanta.variance}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* VISUAL B — Quilates Recuperados vs Plano */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="font-semibold text-slate-700 text-xs">{language === 'pt' ? 'Quilates Recuperados vs Plano' : 'Carats Recovered vs Plan'}</h4>
                  <p className="text-[10px] text-slate-400">{language === 'pt' ? 'Output convertido pelo circuito de recuperação' : 'Output converted by recovery circuit'}</p>
                </div>
                <div className="flex items-center gap-3 text-[10px]">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm bg-primary"></div>
                    <span className="text-slate-600">{language === 'pt' ? 'Real' : 'Actual'}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm bg-primary/30 border border-primary/50"></div>
                    <span className="text-slate-600">{language === 'pt' ? 'Orçamento' : 'Budget'}</span>
                  </div>
                </div>
              </div>
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.throughput.monthly} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 10 }} />
                    <YAxis 
                      tick={{ fill: '#64748b', fontSize: 10 }} 
                      tickFormatter={(v) => `${(v/1000).toFixed(1)}K`}
                      label={{ value: 'ct', angle: -90, position: 'insideLeft', style: { fontSize: 9, fill: '#94a3b8' } }}
                    />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#0A192F', border: 'none', borderRadius: '8px', fontSize: '11px' }}
                      labelStyle={{ color: '#C5A059' }}
                      formatter={(value) => [`${value.toLocaleString()} ct`, '']}
                    />
                    <Bar dataKey="quilatesReal" name={language === 'pt' ? 'Quilates Recuperados Real' : 'Actual Carats Recovered'} fill="#0A192F" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="quilatesOrc" name={language === 'pt' ? 'Quilates Recuperados Orçamento' : 'Budget Carats Recovered'} fill="#0A192F40" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {/* YTD Summary for Quilates */}
              <div className="mt-3 p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-slate-500 font-medium">{language === 'pt' ? 'Acumulado Período:' : 'Period YTD:'}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-700">
                      {data.throughput.ytd.quilates.real.toLocaleString()} vs {data.throughput.ytd.quilates.orc.toLocaleString()} ct
                    </span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${data.throughput.ytd.quilates.variance >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                      {data.throughput.ytd.quilates.variance > 0 ? '+' : ''}{data.throughput.ytd.quilates.variance}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Reading Note */}
          <div className="mt-4 p-3 bg-primary/5 rounded-lg border border-primary/10">
            <p className="text-[11px] text-slate-600 flex items-start gap-2">
              <Info className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
              <span>
                <strong className="text-primary">{language === 'pt' ? 'Leitura:' : 'Reading:'}</strong>
                {language === 'pt' 
                  ? ' Comparar primeiro o minério tratado real vs plano (visual esquerdo). Depois verificar se os quilates recuperados (visual direito) acompanharam o volume tratado. Se o desvio dos quilates for maior que o desvio do minério, o problema está na conversão; se for igual ou menor, o problema está no throughput.'
                  : ' First compare actual ore processed vs plan (left visual). Then check if recovered carats (right visual) followed processed volume. If carat deviation is greater than ore deviation, the problem is in conversion; if equal or less, the problem is in throughput.'}
              </span>
            </p>
          </div>
          
          <p className="text-[10px] text-slate-400 mt-3 text-center">
            {language === 'pt' ? 'Fonte: Relatórios Mensais de Produção' : 'Source: Monthly Production Reports'}
          </p>
        </div>
      )}

      {/* ============ BLOCO 4: PRINCIPAIS CONSTRANGIMENTOS DA PLANTA ============ */}
      {is2026 && data.constrangimentos && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 text-sm">{language === 'pt' ? 'Principais Constrangimentos da Planta' : 'Main Plant Constraints'}</h3>
              <p className="text-[10px] text-slate-500">{language === 'pt' ? 'O que impediu a planta de entregar melhor no período?' : 'What prevented the plant from delivering better in the period?'}</p>
            </div>
          </div>
          
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left py-2 px-3 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">{language === 'pt' ? 'Causa Documentada' : 'Documented Cause'}</th>
                  <th className="text-left py-2 px-3 font-semibold text-slate-500 uppercase tracking-wider text-[10px]">{language === 'pt' ? 'Efeito no Período' : 'Period Effect'}</th>
                  <th className="text-center py-2 px-2 font-semibold text-slate-500 uppercase tracking-wider text-[10px] w-20">{language === 'pt' ? 'Impacto' : 'Impact'}</th>
                </tr>
              </thead>
              <tbody>
                {data.constrangimentos.map((item, idx) => (
                  <tr key={idx} className={`border-t border-slate-100 ${idx % 2 === 0 ? 'bg-slate-50/30' : ''}`}>
                    <td className="py-2.5 px-3">
                      <span className="text-slate-700">{item.causa[language]}</span>
                    </td>
                    <td className="py-2.5 px-3">
                      <span className="text-slate-600">{item.efeito[language]}</span>
                    </td>
                    <td className="py-2.5 px-2 text-center">
                      <SeverityBadge severity={item.severidade} language={language} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-slate-400 mt-2.5 flex items-center gap-1">
            <FileText className="w-3 h-3" />
            {language === 'pt' ? 'Fonte: Relatórios Mensais de Produção e reuniões de acompanhamento operacional' : 'Source: Monthly Production Reports and operational follow-up meetings'}
          </p>
        </div>
      )}

      {/* ============ BLOCO 5: PONTE OPERAÇÃO → LEITURA FINANCEIRA (qualitativa) ============ */}
      <div className="bg-gradient-to-br from-[#0A192F] to-[#112240] rounded-xl p-5">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-8 h-8 rounded-lg bg-gold-500/20 flex items-center justify-center">
            <TrendingUp className="w-4 h-4 text-gold-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white text-sm">{language === 'pt' ? 'Ponte Operação → Leitura Financeira' : 'Operations → Financial Reading Bridge'}</h3>
            <p className="text-[10px] text-slate-400">{language === 'pt' ? 'Tradução qualitativa dos desvios operacionais' : 'Qualitative translation of operational deviations'}</p>
          </div>
        </div>
        
        <div className="overflow-hidden rounded-lg border border-white/10">
          <table className="w-full">
            <thead>
              <tr className="bg-white/5">
                <th className="text-left py-2 px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{language === 'pt' ? 'Desvio Operacional' : 'Operational Deviation'}</th>
                <th className="text-left py-2 px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{language === 'pt' ? 'Implicação Operacional' : 'Operational Implication'}</th>
                <th className="text-left py-2 px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{language === 'pt' ? 'Leitura Financeira' : 'Financial Reading'}</th>
                <th className="text-center py-2 px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider w-20">{language === 'pt' ? 'Pressão' : 'Pressure'}</th>
              </tr>
            </thead>
            <tbody>
              {data.ponteFinanceira.map((item, idx) => (
                <tr key={idx} className={`border-t border-white/5 ${idx % 2 === 0 ? 'bg-white/[0.02]' : ''}`}>
                  <td className="py-2.5 px-3">
                    <span className={`text-xs font-medium ${
                      item.severidade === 'low' ? 'text-emerald-400' :
                      item.severidade === 'medium' ? 'text-amber-400' :
                      'text-rose-400'
                    }`}>
                      {item.desvio[language]}
                    </span>
                  </td>
                  <td className="py-2.5 px-3">
                    <span className="text-[11px] text-slate-300">{item.implicacaoOperacional[language]}</span>
                  </td>
                  <td className="py-2.5 px-3">
                    <span className="text-[11px] text-slate-300">{item.leituraFinanceira[language]}</span>
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${
                      item.severidade === 'low' ? 'bg-emerald-500/20 text-emerald-400' :
                      item.severidade === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-rose-500/20 text-rose-400'
                    }`}>
                      {item.severidade === 'low' ? (language === 'pt' ? 'Limitada' : 'Limited') :
                       item.severidade === 'medium' ? (language === 'pt' ? 'Moderada' : 'Moderate') :
                       (language === 'pt' ? 'Relevante' : 'Relevant')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-slate-500 mt-3 flex items-center gap-1">
          <FileText className="w-3 h-3" />
          {language === 'pt' ? 'Nota: Leitura qualitativa — quantificação financeira requer análise dedicada' : 'Note: Qualitative reading — financial quantification requires dedicated analysis'}
        </p>
      </div>
    </div>
  );
};

export default PlantRecovery;
